import React, { useEffect, useState } from "react";
import {
  fetchFoldersByParent,
  deleteFolder,
  deleteFile,
  fetchFolderById,
  downloadFolderAsZip,
  downloadSelectedFiles,
  deleteMultipleFiles,
} from "../services/folderService";

import FolderForm from "../components/FolderForm";

import ConfirmModal from "./common/ConfirmModal";
import { useToast } from "../context/ToastContext";
import { extractErrorMessage } from "../utils/errorUtils";
import FolderCard from "./FolderCard";
import "./FolderList.css";
import FileUpload from "../components/FileUpload";
import FileEntryCard from "./FileEntryCard";
import { triggerZipDownload } from "../services/download";
import Loader from "./common/Loader";
import LinkUpload from "../components/LinkUpload";
import { canDelete } from "../utils/permissions";
import { can, FEATURE_KEYS } from "../utils/featureAccess";
import { FiArrowLeft, FiCornerUpLeft, FiList, FiGrid, FiCheckSquare, FiTrash2, FiDownload } from "react-icons/fi";
import Breadcrumbs from "./common/Breadcrumbs";
import ManageFolderTile from "./common/ManageFolderTile"; // contains link, folder and file upload

function FolderList({
  currentFolderId,
  currentFolderPath,
  setCurrentFolderId,
  setCurrentFolderPath,
  refreshFolders,
  user,
  employeeId,
  mode = "employee",
}) {
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [folderHistory, setFolderHistory] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);
  const { addToast } = useToast();
  const [fileViewMode, setFileViewMode] = useState("grid");
  const [currentFolder, setCurrentFolder] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [manageActive, setManageActive] = useState(null);
  const atRoot = currentFolderId === "root";
  // File-manager management is gated on the File Manager feature's capabilities,
  // not on role. Write = create/upload/add-link; delete = remove files/folders.
  const canWrite = can(user, FEATURE_KEYS.FILE_MANAGER, "write");
  const canDeleteItems = can(user, FEATURE_KEYS.FILE_MANAGER, "delete");

  useEffect(() => {
    refreshCurrentFolder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFolderId, employeeId]);

  useEffect(() => {
    setManageActive(null);
  }, [currentFolderId, currentFolderPath]);

  const clearSelectionState = () => {
    setSelectMode(false);
    setSelectedFiles([]);
  };

  const goToRoot = () => {
    setFolderHistory([]);
    clearSelectionState();
    setCurrentFolderId("root");
    setCurrentFolderPath("root");
  };

  const enterFolder = (folderId, path) => {
    setFolderHistory((prev) => {
      if (prev.length > 0 && prev[prev.length - 1].id === currentFolderId) return prev;
      return [...prev, { id: currentFolderId, path: currentFolderPath || "root" }];
    });
    clearSelectionState();
    setCurrentFolderId(folderId);
    setCurrentFolderPath(path);
  };

  const historyCrumbs = folderHistory
    .filter((h) => h.path !== "root")
    .map((h) => ({
      id: h.id,
      path: h.path,
      label: h.path.split("/").pop(),
    }));

  const currentLabel = currentFolder?.name || (currentFolderPath || "root").split("/").pop();

  let crumbs = [];
  if (currentFolder) {
    crumbs.push({ id: "root", path: "root", label: "Home" });
    if (currentFolder.ancestors) {
      currentFolder.ancestors.forEach(a => {
        crumbs.push({ id: a.id, path: "folder", label: a.name });
      });
    }
    if (currentFolderId !== "root") {
      crumbs.push({
        id: currentFolder.id,
        path: currentFolder.path || "root",
        label: currentFolder.name
      });
    }
  } else {
    crumbs = [
      { id: "root", path: "root", label: "Home" },
      ...historyCrumbs,
      { id: currentFolderId, path: currentFolderPath, label: currentLabel },
    ];
  }

  crumbs = crumbs.filter((c, index, self) =>
    index === self.findIndex((t) => (t.id === c.id))
  );

  const handleCrumbClick = (idx, crumb) => {
    setCurrentFolderId(crumb.id);
    setCurrentFolderPath(crumb.path || "root");
    setSelectMode(false);
    setSelectedFiles([]);
    if (crumb.id === "root") {
      setFolderHistory([]);
    } else {
      const histIdx = folderHistory.findIndex(h => h.id === crumb.id);
      if (histIdx !== -1) {
        setFolderHistory(folderHistory.slice(0, histIdx));
      }
    }
  };

  const handleDeleteFile = async (file) => {
    if (!canDelete(file, user)) {
      addToast("You can only delete files you created.", "error");
      return;
    }
    try {
      await deleteFile(currentFolderId, file.id);
      addToast(`Deleted file: ${file.name}`, "success");
    } catch (err) {
      const msg = await extractErrorMessage(err, "Error deleting file");
      addToast(msg, "error");
    } finally {
      refreshCurrentFolder();
    }
  };

  const handleDownloadAll = async () => {
    try {
      setDownloading(true);
      const blob = await downloadFolderAsZip(currentFolderId);
      triggerZipDownload(blob, `${currentFolder?.name || "folder"}.zip`);
    } catch (err) {
      const msg = await extractErrorMessage(err, "Failed to download folder ZIP.");
      addToast(msg, "error");
    } finally {
      setDownloading(false);
    }
  };

  const handleBulkDelete = async () => {
    const deletable = selectedFiles.filter((f) => canDelete(f, user));
    const blocked = selectedFiles.filter((f) => !canDelete(f, user));
    if (deletable.length === 0) {
      addToast("You can only delete files you created.", "error");
      return;
    }
    const names = deletable.map((f) => f.name);
    try {
      await deleteMultipleFiles(currentFolderId, names);
      addToast("Files deleted successfully", "success");
      setSelectedFiles([]);
      refreshCurrentFolder();
    } catch (err) {
      const msg = await extractErrorMessage(err, "Bulk delete failed");
      addToast(msg, "error");
    } finally {
      if (blocked.length > 0) {
        addToast(`Skipped ${blocked.length} file(s) you don't own.`, "info");
      }
    }
  };

  const handleBulkDownload = async () => {
    const names = selectedFiles.map((f) => f.name);
    try {
      const blob = await downloadSelectedFiles(currentFolderId, names);
      triggerZipDownload(blob, "selected-files.zip");
    } catch (err) {
      const msg = await extractErrorMessage(err, "Download failed");
      addToast(msg, "error");
    }
  };

  const goBack = () => {
    clearSelectionState();
    if (folderHistory.length === 0) {
      goToRoot();
      return;
    }
    const last = folderHistory[folderHistory.length - 1];
    setFolderHistory((prev) => prev.slice(0, -1));
    setCurrentFolderId(last.id || "root");
    setCurrentFolderPath(last.path || "root");
  };

  const refreshCurrentFolder = async () => {
    try {
      setLoading(true);
      const [folderData, currentFolderData] = await Promise.all([
        fetchFoldersByParent(currentFolderId),
        fetchFolderById(currentFolderId),
      ]);
      setFolders(folderData);
      setCurrentFolder(currentFolderData);
    } catch (err) {
      setError(`Failed to load folders: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFolder = async (folderId) => {
    try {
      await deleteFolder(folderId);
      addToast("Folder deleted successfully", "success");
      setFolders((prevFolders) =>
        prevFolders.filter((folder) => folder.id !== folderId)
      );
    } catch (err) {
      const msg = await extractErrorMessage(err, "Failed to delete folder");
      addToast(msg, "error");
    }
  };

  const fileCount = currentFolder?.files?.length || 0;
  const folderCount = folders.length;
  const isEmpty = folderCount === 0 && fileCount === 0;

  const toggleFileSelection = (file) => {
    setSelectedFiles((prev) =>
      prev.some((f) => f.url === file.url)
        ? prev.filter((f) => f.url !== file.url)
        : [...prev, file]
    );
  };

  if (loading) return <Loader message="Fetching folders..." />;
  if (error) return <p style={{ color: "red" }}>{error}</p>;
  const titleText = (currentFolderId === "root")
    ? "Folders"
    : (currentFolder?.name || currentLabel);

  return (
    <>
      <div className={`folder-container ${atRoot ? "root-folder-view" : ""}`}>
        <div className="folder-header-section">
          {!atRoot && (
            <div className="header-breadcrumbs compact-top">
              <Breadcrumbs crumbs={crumbs} onCrumbClick={handleCrumbClick} />
            </div>
          )}

          <div className="folder-header-band">
            <div className="folder-title-row">
              <div className="folder-title-block">
                <h2 className="folder-title">{titleText}</h2>
              </div>
              {loading && <div className="spinner-inline" />}
            </div>

            {!atRoot && (
              <div className="folder-header-actions">
                <div className="folder-nav-buttons">
                  <button
                    className="btn-glassy-nav"
                    onClick={goBack}
                    disabled={atRoot}
                    title={atRoot ? "No previous folder" : "Go back"}
                  >
                    <FiArrowLeft className="btn-icon" />
                    <span>Back</span>
                  </button>
                  <button
                    className="btn-glassy-nav primary"
                    onClick={goToRoot}
                    title="Go to root"
                  >
                    <FiCornerUpLeft className="btn-icon" />
                    <span>Back to Root</span>
                  </button>
                </div>
                {canWrite && (
                  <ManageFolderTile
                    variant="inline"
                    showTitle={false}
                    controlsOnly
                    folderId={currentFolderId}
                    currentPath={currentFolderPath}
                    user={user}
                    canManage={canWrite}
                    active={manageActive}
                    onActiveChange={setManageActive}
                  />
                )}
              </div>
            )}
          </div>

          {!atRoot && canWrite && manageActive && (
            <div className="manage-panel-slot">
              {manageActive === "create" && (
                <FolderForm
                  parentId={currentFolderId}
                  onSuccess={() => {
                    refreshCurrentFolder();
                    setManageActive(null);
                  }}
                />
              )}
              {manageActive === "upload" && (
                <FileUpload
                  folderId={currentFolderId}
                  onSuccess={() => {
                    refreshCurrentFolder();
                    setManageActive(null);
                  }}
                />
              )}
              {manageActive === "link" && (
                <LinkUpload
                  folderId={currentFolderId}
                  onSuccess={() => {
                    refreshCurrentFolder();
                    setManageActive(null);
                  }}
                />
              )}
            </div>
          )}
        </div>

        <div className="folder-content-wrapper">
          <section className="folder-panel contents-panel">
            {!atRoot && (
              <div className="folder-panel-header">
                <div className="contents-heading">
                  <h3 className="section-title">Contents</h3>
                  <span className="contents-count">
                    {folderCount} {folderCount === 1 ? "folder" : "folders"}
                    {" · "}
                    {fileCount} {fileCount === 1 ? "item" : "items"}
                  </span>
                </div>
                <div className="contents-tools">
                  <button
                    className={`btn-tool ${fileViewMode === "grid" ? "active" : ""}`}
                    onClick={() => setFileViewMode("grid")}
                    title="Grid view"
                    aria-label="Grid view"
                  >
                    <FiGrid />
                  </button>
                  <button
                    className={`btn-tool ${fileViewMode === "list" ? "active" : ""}`}
                    onClick={() => setFileViewMode("list")}
                    title="List view"
                    aria-label="List view"
                  >
                    <FiList />
                  </button>
                  <button
                    className={`btn-tool ${selectMode ? "active" : ""}`}
                    onClick={() => {
                      setSelectMode(!selectMode);
                      setSelectedFiles([]);
                    }}
                    disabled={fileCount === 0}
                    title="Select files"
                    aria-label="Select files"
                  >
                    <FiCheckSquare />
                  </button>
                  <button
                    className="btn-tool"
                    onClick={handleDownloadAll}
                    disabled={downloading || fileCount === 0}
                    title="Download all as ZIP"
                    aria-label="Download all as ZIP"
                  >
                    <FiDownload />
                  </button>
                </div>
              </div>
            )}

            {!atRoot && selectMode && (
              <div className="contents-bulk-bar">
                <div className="bulk-info">{selectedFiles.length} selected</div>
                <button
                  className="btn-text-only bulk-select-all"
                  onClick={() => setSelectedFiles(currentFolder?.files || [])}
                >
                  Select all files
                </button>
                <div className="bulk-btn-row">
                  {canDeleteItems && (
                    <button className="btn-tiny danger" onClick={handleBulkDelete} title="Delete selected">
                      <FiTrash2 />
                    </button>
                  )}
                  <button className="btn-tiny" onClick={handleBulkDownload} title="Download selected">
                    <FiDownload />
                  </button>
                </div>
              </div>
            )}

            {isEmpty && !loading ? (
              <div className="empty-folder-state">
                <p>{atRoot ? "No folders yet." : "This folder is empty."}</p>
              </div>
            ) : (
              <div className={`contents-container ${fileViewMode}`}>
                {folders.map((folder) => (
                  <FolderCard
                    key={folder.id}
                    folder={folder}
                    user={user}
                    onEnter={enterFolder}
                    onDelete={handleDeleteFolder}
                    canManage={canDeleteItems}
                  />
                ))}
                {currentFolder?.files?.map((file, index) => (
                  <FileEntryCard
                    key={`file-${index}`}
                    file={file}
                    user={user}
                    folderId={currentFolderId}
                    viewMode={fileViewMode}
                    onRefresh={refreshCurrentFolder}
                    onDelete={!selectMode ? () => handleDeleteFile(file) : null}
                    selected={selectedFiles.some((f) => f.url === file.url)}
                    onToggleSelect={selectMode ? () => toggleFileSelection(file) : null}
                  />
                ))}
              </div>
            )}
          </section>
        </div>

        <ConfirmModal
          show={showConfirm}
          title="Delete Folder"
          message="Are you sure you want to delete this folder?"
          onCancel={() => {
            setShowConfirm(false);
            setFolderToDelete(null);
          }}
          onConfirm={async () => {
            await handleDeleteFolder(folderToDelete);
            setShowConfirm(false);
            setFolderToDelete(null);
          }}
        />
      </div>
    </>
  );
}

export default FolderList;
